import { NavigationError } from '@angular/router';

/**
 * Cada navegador redacta a su manera el fallo de un `import()` diferido:
 * Chrome «Failed to fetch dynamically imported module», Firefox «error loading
 * dynamically imported module», Safari «Importing a module script failed».
 * No hay un tipo de error común, solo el texto.
 */
const CHUNK_LOAD_ERROR = /dynamically imported module|module script failed|ChunkLoadError|Loading chunk/i;

/** Ruta ya recargada y cuándo, para no encadenar recargas. */
const LAST_RELOAD_KEY = 'refac.chunkReload';

/** Dentro de esta ventana no se vuelve a recargar la misma ruta. */
const RETRY_WINDOW_MS = 30_000;

/**
 * Recarga la página cuando el router no consigue bajar el chunk de una ruta
 * diferida.
 *
 * Pasa cuando el navegador arrastra código de un despliegue anterior: los
 * `import()` que lleva dentro apuntan a chunks con hashes que ya no están en
 * el servidor (solo se conservan los tres últimos releases) y contestan 404.
 * Recargar trae el index.html vigente —nunca se cachea— y con él los chunks
 * que sí existen.
 */
export function reloadOnChunkError(event: NavigationError): void {
  const message = (event.error as Error | undefined)?.message ?? String(event.error);

  if (!CHUNK_LOAD_ERROR.test(message)) {
    return;
  }

  if (reloadedRecently(event.url)) {
    // Si tras recargar sigue faltando, el chunk no está en el servidor y
    // recargar de nuevo solo mete al usuario en un bucle.
    console.error(`No se pudo cargar el módulo de ${event.url}`, event.error);
    return;
  }

  rememberReload(event.url);

  // `assign` y no `reload`: la navegación falló, así que la URL del navegador
  // sigue siendo la anterior y con `reload` se volvería a la pantalla de
  // partida en vez de a la que se pidió.
  window.location.assign(event.url);
}

function reloadedRecently(url: string): boolean {
  const raw = readSession(LAST_RELOAD_KEY);
  if (!raw) {
    return false;
  }

  const [reloadedUrl, at] = raw.split('|');
  return reloadedUrl === url && Date.now() - Number(at) < RETRY_WINDOW_MS;
}

function rememberReload(url: string): void {
  writeSession(LAST_RELOAD_KEY, `${url}|${Date.now()}`);
}

// sessionStorage lanza en modo privado de algunos navegadores. Si no está
// disponible se pierde la marca antibucle, pero recargar sigue siendo mejor
// que dejar la pantalla en blanco.
function readSession(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* sin almacenamiento no hay marca; se asume el riesgo de una recarga extra */
  }
}
